import React from 'react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../components/ContactForm';

const Contact = () => {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="hemp-bg-gradient py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center p-8 bg-gradient-to-br from-white to-hemp-green-50 rounded-2xl shadow-2xl">
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight drop-shadow-lg">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-gray-800 max-w-3xl mx-auto font-medium bg-white/80 px-4 py-3 rounded-lg">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="hemp-card mb-8">
                <h2 className="text-2xl font-bold text-hemp-green-800 mb-6">
                  {t('contact.info.title')}
                </h2>
                
                <div className="space-y-6">
                  {/* Address */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-hemp-green-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-hemp-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {t('contact.info.address')}
                      </h3>
                      <p className="text-gray-600">
                        {t('contact.info.addressValue')}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-hemp-brown-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-hemp-brown-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {t('contact.info.phone')}
                      </h3>
                      <p className="text-gray-600">
                        {t('contact.info.phoneValue')}
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-hemp-leaf-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-hemp-leaf-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {t('contact.info.email')}
                      </h3>
                      <p className="text-gray-600">
                        {t('contact.info.emailValue')}
                      </p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 bg-hemp-green-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-hemp-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {t('contact.info.hours')}
                      </h3>
                      <p className="text-gray-600">
                        {t('contact.info.hoursValue')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="hemp-card">
                <h2 className="text-2xl font-bold text-hemp-green-800 mb-4">
                  {t('contact.emergency.title')}
                </h2>
                <p className="text-gray-600 mb-4">
                  {t('contact.emergency.description')}
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold text-red-800">
                      {t('contact.emergency.phone')}
                    </span>
                  </div>
                  <p className="text-red-700 mt-2">
                    {t('contact.emergency.phoneValue')}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="hemp-card">
                <h2 className="text-2xl font-bold text-hemp-green-800 mb-6">
                  {t('contact.form.title')}
                </h2>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
